import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PagePaper } from "../Paper";
import { useApiClient } from "@/hooks/useApiClient";
import { useEffect, useState } from "react";
import { usePrefectureOptions } from "@/features/realEstateReceptionBookFeed/hooks/usePrefectureOptions";
import {
  getSelectNextLocation,
  type IAddressTree,
} from "@/api/getSelectNextLocation";
import { ButtonColorOption, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "../Wrapper";
import { type CityListResponse } from "@/types/city";
import { toast } from "react-toastify";

interface IProps {
  open: boolean;
  onClose: () => void;
  prefectureId: number;
  cityInfo?: {
    setCode: (cityCode: string) => void; // setCityはページから呼ばれた場合に設定され、モーダルから呼ばれた場合は設定されない
    listResponse: CityListResponse;
  };
  setLocationWithoutCity: (locationWithoutCity: string) => void;
  setInputTextDirectly?: React.Dispatch<React.SetStateAction<boolean>>; // setCityが設定されている場合は、setInputTextDirectlyも必須
}

const SELECT_CITY_COUNT = 2;

const DISPLAY_COLUMNS_COUNT = 4;
let resultCity = "";
// Cityを外したLocationと命名していますが、モーダルの場合はCityも含めた値を保持する
let resultLocationWithoutCity = "";

export const SelectLocationModal: React.FC<IProps> = (props) => {
  const {
    open,
    onClose,
    prefectureId,
    cityInfo,
    setLocationWithoutCity,
    setInputTextDirectly,
  } = props;

  const apiClient = useApiClient().apiClient;
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedLocations, setSelectedLocations] = useState<
    Array<{ value: string | null; label: string }>
  >([]);
  const [fetchLocationList, setFetchLocationList] = useState<IAddressTree[]>(
    []
  );
  const [displayLocationList, setDisplayLocationList] = useState<
    IAddressTree[][]
  >([]);
  const { prefectureOptions } = usePrefectureOptions();
  const [searchText, setSearchText] = useState<string>("");

  const handleClickLocationLabelInDialog: (
    item: IAddressTree
  ) => Promise<void> = async (item: IAddressTree) => {
    setLoading(true);
    setSearchText("");
    setSelectedLocations((prev) => [
      ...prev,
      { value: String(item.refId), label: item.name },
    ]);

    if (cityInfo) {
      if (!resultCity) {
        resultCity = item.name;
      } else {
        resultLocationWithoutCity = resultLocationWithoutCity + item.name;
      }
    } else {
      resultLocationWithoutCity = resultLocationWithoutCity + item.name;
    }

    const response = await getSelectNextLocation(
      apiClient,
      prefectureId,
      item.refId
    );
    if (response.list.length) {
      setFetchLocationList(response.list);
      setDisplayLocationList(splitData(response.list));
    } else {
      const removeSuffix = ["大字なし", "小字なし"];
      for (const suffix of removeSuffix) {
        if (resultLocationWithoutCity.endsWith(suffix)) {
          resultLocationWithoutCity = resultLocationWithoutCity.slice(
            0,
            -suffix.length
          );
        }
      }

      if (cityInfo && setInputTextDirectly) {
        const targetCity = cityInfo.listResponse.list.find(
          (city) => city.name === resultCity
        );
        if (targetCity) {
          cityInfo.setCode(targetCity.cityCode);
          setInputTextDirectly(true); // 市区町村が見つかればtrue
        } else {
          toast.info("取り扱っていない市区町村です。再度選択してください。");
          cityInfo.setCode("");
          resultLocationWithoutCity = "";
          setInputTextDirectly(false); // 市区町村が見つかればfalse
        }
      }
      setLocationWithoutCity(resultLocationWithoutCity);
      resultCity = "";
      resultLocationWithoutCity = "";
      setFetchLocationList([]);
      setDisplayLocationList([[]]);
      setPrefectureNameInSelectedLocations();
      onClose();
    }
    setLoading(false);
  };

  const splitData = (data: IAddressTree[]): IAddressTree[][] => {
    const chunked = [];
    for (let i = 0; i < data.length; i += DISPLAY_COLUMNS_COUNT) {
      chunked.push(data.slice(i, i + DISPLAY_COLUMNS_COUNT));
    }
    return chunked;
  };

  const handleBack: () => Promise<void> = async () => {
    if (selectedLocations.length < SELECT_CITY_COUNT) {
      return;
    }
    setLoading(true);
    setSearchText("");
    const newSelectedLocations = [...selectedLocations];
    newSelectedLocations.pop();
    setSelectedLocations(newSelectedLocations);
    // NOTE: newSelectedLocationsの長さが1の時とそうでない時でAPIのパスが変わる
    let response;
    if (newSelectedLocations.length < SELECT_CITY_COUNT) {
      if (cityInfo) {
        resultCity = "";
        resultLocationWithoutCity = "";
      }
      response = await getSelectNextLocation(apiClient, prefectureId);
    } else {
      const refId =
        newSelectedLocations[newSelectedLocations.length - 1].value ??
        undefined;
      response = await getSelectNextLocation(
        apiClient,
        prefectureId,
        Number(refId)
      );
    }
    setFetchLocationList(response.list);
    setDisplayLocationList(splitData(response.list));
    resultLocationWithoutCity = resultLocationWithoutCity.slice(
      0,
      -selectedLocations[selectedLocations.length - 1].label.length
    );
    setLoading(false);
  };

  const setPrefectureNameInSelectedLocations: () => void = () => {
    const prefectureName = prefectureOptions.find(
      (prefecture) => prefecture.value === String(prefectureId)
    )?.label;
    if (prefectureName) {
      setSelectedLocations([{ value: null, label: prefectureName }]);
    }
  };

  useEffect(() => {
    setPrefectureNameInSelectedLocations();
  }, [prefectureId]);

  useEffect(() => {
    const fetchLocations: () => Promise<void> = async () => {
      setLoading(true);
      setSearchText("");
      const locations = await getSelectNextLocation(apiClient, prefectureId);
      setFetchLocationList(locations.list);
      setDisplayLocationList(splitData(locations.list));
      setLoading(false);
    };

    (async () => {
      if (open) {
        resultCity = "";
        resultLocationWithoutCity = "";
        setPrefectureNameInSelectedLocations();
        await fetchLocations();
      }
    })();
  }, [open]);

  useEffect(() => {
    const filteredLocations = fetchLocationList.filter((location) =>
      location.name.toLowerCase().includes(searchText.toLowerCase())
    );
    setDisplayLocationList(splitData(filteredLocations));
  }, [searchText]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          height: "80vh",
          width: "80vw",
          boxShadow: 24,
          outline: "none",
          border: "1px solid",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 2,
            py: 2,
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ color: "common.white" }}
          >
            所在を選択してください
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            pt: 4,
          }}
        >
          <Typography sx={{ pl: 3 }}>
            {selectedLocations.map((location) => location.label).join(" > ")}
          </Typography>

          <Box sx={{ mt: 3, px: 4 }}>
            <TextField
              label="所在検索"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              fullWidth
            />
          </Box>

          {loading ? (
            <Stack alignItems="center">
              <CircularProgress sx={{ my: "20px" }} />
            </Stack>
          ) : (
            <TableContainer component={PagePaper}>
              <Table stickyHeader>
                <TableBody>
                  {displayLocationList.map((location, locationIndex) => (
                    <TableRow key={`location-${locationIndex}`}>
                      {location.map((cell, cellIndex) => (
                        <TableCell
                          key={`cell-${locationIndex}-${cellIndex}`}
                          onClick={() => {
                            void handleClickLocationLabelInDialog(cell);
                          }}
                          sx={{
                            cursor: "pointer",
                            textAlign: "center",
                            border: "1px solid",
                            borderColor: "divider",
                            "&:hover": {
                              backgroundColor: "primary.light",
                              color: "common.white",
                            },
                          }}
                        >
                          <Typography>{cell.name}</Typography>
                        </TableCell>
                      ))}
                      {Array.from({
                        length: DISPLAY_COLUMNS_COUNT - location.length,
                      }).map((_, i) => (
                        <TableCell
                          key={`empty-${i}`}
                          sx={{
                            border: "none",
                            borderColor: "transparent",
                          }}
                        />
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            textAlign: "right",
            borderTop: "1px solid #ddd",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => {
              void handleBack();
            }}
            variant={ButtonVariantOption.Outlined}
            color={ButtonColorOption.Primary}
            disabled={selectedLocations.length < SELECT_CITY_COUNT}
            size={SizingWrapperStyle.LARGE}
          >
            戻る
          </Button>
          <Button
            onClick={onClose}
            variant={ButtonVariantOption.Outlined}
            color={ButtonColorOption.Error}
            size={SizingWrapperStyle.LARGE}
          >
            閉じる
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
